import { NgClass, NgTemplateOutlet  } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, ViewChild } from '@angular/core'
import {
  flashAnimation,
  fadeInAnimation,
  fadeOutAnimation,
  fadeInOnEnterAnimation,
  fadeOutOnLeaveAnimation,
  headShakeAnimation,
  shakeAnimation,
} from 'angular-animations'
import { takeUntil } from 'rxjs'

import {
  SafePipe,
  PlayButtonComponent,
  SlidePanelComponent
} from '@nx-superprep/shared'

import { FrontendCard as Card, Choice } from '@nx-superprep/backend/firebase'

import { GameplayBaseComponent, waitForDelay } from '../gameplay-base'
import { log } from '@nx-superprep/utils'
import { CarouselModule } from 'ngx-bootstrap/carousel'
import { MatCardModule } from '@angular/material/card'
import { MatGridListModule } from '@angular/material/grid-list'

@Component({
  standalone: true,
  selector: 'gameplay-stack',
  templateUrl: './stack.component.html',
  styleUrls: ['./stack.component.scss'],
  animations: [
    flashAnimation(),
    fadeInAnimation(),
    fadeOutAnimation(),
    fadeInOnEnterAnimation(),
    fadeOutOnLeaveAnimation(),
    headShakeAnimation({ duration: 900, delay: 0 }),
    shakeAnimation({ duration: 200, delay: 0, translate: '3px' }),
  ],
  imports: [
    NgClass, NgTemplateOutlet, SafePipe,
    MatCardModule,
    MatGridListModule,
    CarouselModule,
    SlidePanelComponent, PlayButtonComponent
  ]
})
export class StackComponent extends GameplayBaseComponent implements AfterViewInit {
  override title = 'stack'

  @ViewChild('video') video: ElementRef<HTMLVideoElement> | undefined;
  @HostListener('window:touchstart', ['$event'])
  onTouchStart() {
    if (!this.video) { return }
    const videoElement = this.video.nativeElement;
    if (videoElement.paused) {
      log.debug('video started')
      videoElement.play();
    }
  }

  ngAfterViewInit() {
    if (!this.video) { return }
    const videoElement = this.video.nativeElement;

    // Ensure the video is muted and then try to play
    videoElement.muted = true;

    // Manually trigger playback if necessary (for Safari iOS)
    videoElement.play().catch(err => {
      log.warn('Autoplay prevented: ', err);
      // handle the autoplay rejection on Safari iOS
    });
  }

  interval = 1500
  showIndicators = false

  fittedChoices: Choice[] = []

  get isVisualMedia() {
    return this.didAnswer
    ? this.answer?.isImage || this.answer?.isVideo
    : this.question?.isImage || this.question?.isVideo
  }

/*
  get headerContainerHeight() {
    return this.shouldShowTextContent
      || this.hasTranslation
      || this.hasPronunciation
      ? (this.shouldShowPlayButton && this.isMediaVisible
        ? '350px'
        : (this.hasTranslation || this.hasPronunciation) ? '250px' : '100px')
      : '120px'
  }
*/

  get videoUrl(): string {
    const url = (this.didAnswer ? this.answer : this.question)?.videoUrl
    return `${url}`
  }

  /**
   * Determines if media (video or image) should be visible based on the current question or answer.
   * Returns true if the question or answer has media content.
   */
  get isMediaVisible() {
    return (this.question?.isVideo || (this.didAnswer && this.answer?.hasVideo))
      || (this.question?.isImage || (this.didAnswer && this.answer?.isImage))
      || (this.didAnswer && this.answer?.isElement)
  }

  /**
   * Returns the end text based on whether an answer has been given.
   * If an answer has been given, it returns the result from the parent class's `endText` method.
   * Otherwise, returns the result from the `bottomText` method.
   */
  override endText() {
    return this.didAnswer ? super.endText() : this.bottomText() //isEmpty(super.endText()) ? gameplaySpacing.smallSpacer : super.endText()
  }

  /**
   * Handles the selection of a choice.
   * Calls the parent class's `onSelect` method and then waits for 800 milliseconds before deactivating the choice.
   * Uses `waitForDelay` with `takeUntil` for unsubscribing when the component is destroyed.
   * @param choice The selected choice.
   */
  override onSelect(choice: Choice): void {
    super.onSelect(choice)
    waitForDelay(800)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => choice.isActive = false)
  }

  /**
   * Initializes the state of the component based on the provided card.
   * If the superclass `initState` method returns false, it terminates further processing.
   * Ensures there are at least three choices; adds an empty choice if needed.
   * @param card The card used to initialize the state.
   * @returns True if the state was successfully initialized, otherwise false.
   */
  override initState(card: Card) {
    if (!super.initState(card)) { return false }
    this.fittedChoices = this.choices.length < 3 ? [...this.choices, new Choice({})] : this.choices
    return true
  }
}
